import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
    Stack,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import { useGetCaseDatabaseVersion, useGetAndStartCaseDatabaseStatus, migrateCaseDatabase } from '../../api/database_status';
import { formatDateTime } from '../../utils/formatters';

// What started the most recent migration request (CaseDBVersion.migration_trigger).
const TRIGGER_LABELS = {
    auto_on_wake: 'Automatic (case opened)',
    manual: 'Manual',
    restore: 'Case reactivation',
};

// queued = a step is on the queue; started = a worker is applying a step.
const isInFlight = (status) => status === 'started' || status === 'queued';

export default function CaseDatabaseMigration() {
    const dbStatus = useGetAndStartCaseDatabaseStatus();
    const [migrationInitiated, setMigrationInitiated] = useState(false);
    const [optimisticTargetVersion, setOptimisticTargetVersion] = useState(null);

    // Always fetch - let SWR handle errors and keep previous data
    const { data: versionData, isLoading, error, mutate } = useGetCaseDatabaseVersion(true);

    // Check if database is online for button state
    const isDatabaseOnline = dbStatus.data?.status?.status_code !== "unreachable";

    // Override display if we initiated migration but backend hasn't confirmed yet
    const displayData = migrationInitiated && versionData ? {
        ...versionData,
        migration_status: 'started',
        target_version: optimisticTargetVersion || versionData.target_version,
        is_current_version: false,
        _updateDate: versionData._updateDate || new Date().toISOString()
    } : versionData;

    // Clear the migration flag once backend confirms
    useEffect(() => {
        if (migrationInitiated && versionData &&
            (isInFlight(versionData.migration_status) || versionData.migration_status === 'completed')) {
            setMigrationInitiated(false);
            setOptimisticTargetVersion(null);
        }
    }, [versionData, migrationInitiated]);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleMigrateClick = () => {
        setConfirmOpen(true);
    };

    const handleConfirmMigrate = async () => {
        setConfirmOpen(false);
        setMigrating(true);
        try {
            const result = await migrateCaseDatabase();

            // Set flag and target version for display override
            setMigrationInitiated(true);
            setOptimisticTargetVersion(result.target_version);

            // Also update SWR cache optimistically
            await mutate(
                {
                    ...versionData,
                    migration_status: 'started',
                    target_version: result.target_version,
                    is_current_version: false,
                    _updateDate: new Date().toISOString()
                },
                false
            );

            setSnackbar({ open: true, message: result.message, severity: result.started === false ? 'info' : 'success' });
        } catch (err) {
            console.error('Migration failed:', err);
            setSnackbar({ open: true, message: 'Failed to start migration', severity: 'error' });
            setMigrationInitiated(false);
            setOptimisticTargetVersion(null);
        } finally {
            setMigrating(false);
        }
    };

    const handleCancelMigrate = () => {
        setConfirmOpen(false);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Use displayData for all UI decisions
    const isMigrateDisabled =
        !isDatabaseOnline ||
        !dbStatus.data?.status ||
        isLoading ||
        !displayData ||
        displayData.is_current_version ||
        isInFlight(displayData.migration_status) ||
        migrating;

    const getStatusChip = () => {
        if (!displayData) return null;

        const { migration_status, is_current_version } = displayData;

        // Order matters: an in-flight migration takes priority. Otherwise,
        // is_current_version is the truth — a stale `migration_status: completed`
        // from a prior version's run shouldn't mask the fact that we now need
        // another migration.
        if (migration_status === 'started') {
            return <Chip label="Migration In Progress" color="warning" size="small" />;
        } else if (migration_status === 'queued') {
            return <Chip label="Migration Queued" color="warning" size="small" />;
        } else if (is_current_version) {
            return <Chip label="Up to Date" color="success" size="small" />;
        } else if (migration_status === 'failed') {
            return <Chip label="Migration Failed" color="error" size="small" />;
        } else if (!isDatabaseOnline || !dbStatus.data?.status) {
            return <Chip label="Database Offline" color="error" size="small" />;
        } else {
            return <Chip label="Update Available" color="info" size="small" />;
        }
    };

    if (error && !displayData) {
        return (
            <Card>
                <CardContent>
                    <Typography color="error">Error loading database version information</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardContent>
                    <Stack spacing={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6">Case Database Version</Typography>
                            {getStatusChip()}
                        </Box>

                        {isLoading && !displayData ? (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading version information...
                                </Typography>
                            </Box>
                        ) : displayData ? (
                            <Stack spacing={1}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Current Version
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {displayData.version}
                                    </Typography>
                                </Box>

                                {displayData.target_version && !displayData.is_current_version && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Target Version
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {displayData.target_version}
                                        </Typography>
                                    </Box>
                                )}

                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Last Updated
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatDateTime(displayData._updateDate || new Date().toISOString())}
                                    </Typography>
                                </Box>

                                {displayData.migration_trigger && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Last Requested By
                                        </Typography>
                                        <Typography variant="body2">
                                            {TRIGGER_LABELS[displayData.migration_trigger] || displayData.migration_trigger}
                                        </Typography>
                                    </Box>
                                )}

                                {displayData.migration_status === 'failed' && displayData.migration_error && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Failure
                                        </Typography>
                                        <Typography variant="body2" color="error">
                                            {displayData.migration_error} — retry with Migrate Database
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        ) : null}
                    </Stack>
                </CardContent>

                <CardActions>
                    <Button
                        variant="contained"
                        onClick={handleMigrateClick}
                        disabled={isMigrateDisabled}
                    >
                        {migrating ? 'Starting Migration...' : 'Migrate Database'}
                    </Button>
                </CardActions>
            </Card>

            <Dialog open={confirmOpen} onClose={handleCancelMigrate}>
                <DialogTitle>Confirm Database Migration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to start the database migration {displayData?.version ? `from version ${displayData.version}` : ''} to the latest version?
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 2 }}>
                        This process will run in the background and may take some time to complete. We will backup the current database state before proceeding.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelMigrate}>Cancel</Button>
                    <Button onClick={handleConfirmMigrate} variant="contained" autoFocus>
                        Start Migration
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}